import React from "react";
import { motion } from "framer-motion";
import { Clock, MessageSquare, ShieldCheck, Award } from "lucide-react";

const ANIRUDH_PHOTO = "https://customer-assets.emergentagent.com/job_boarding-school-fit/artifacts/zjrcjguk_Anirudh%20Gupta.jpg";
const SANDHYA_PHOTO = "https://customer-assets.emergentagent.com/job_boarding-school-fit/artifacts/3pehm1ty_2025-05-22%2021.03.31.jpg";

const testimonials = [
  {
    quote:
      "ESA understood our son better than we expected. They recommended schools we hadn't considered, and he's now thriving at one of them.",
    author: "Parent of Year 9 student",
    location: "Mumbai",
  },
  {
    quote:
      "The interview preparation was exceptional. Our daughter walked into her interview with genuine confidence — not rehearsed answers.",
    author: "Parent of Year 7 student",
    location: "Delhi",
  },
  {
    quote:
      "What impressed us most was their honesty. They told us when a school wasn't the right fit, even when we had our hearts set on it.",
    author: "Parent of Year 10 student",
    location: "Bangalore",
  },
];

const processPoints = [
  {
    icon: Clock,
    title: "Weekly Updates",
    description: "Regular progress calls and status updates throughout your journey.",
  },
  {
    icon: MessageSquare,
    title: "Direct Access",
    description: "Personal WhatsApp and email access to your dedicated advisor.",
  },
  {
    icon: ShieldCheck,
    title: "Honest Guidance",
    description:
      "We will recommend not applying where fit is wrong — even if you disagree.",
  },
];

const founders = [
  {
    name: "Anirudh Gupta",
    role: "Founder & Principal Advisor",
    photo: ANIRUDH_PHOTO,
    credentials: ["MBA, University of Nottingham", "British Council Certified Counsellor", "Master Career Guide"],
    bio: "With 20+ years in international education advisory, Anirudh leads strategy for elite boarding school admissions. He guides families on school fit, positioning, timelines, and end-to-end execution. His work combines high standards, clear judgement, and student-first decision making.",
  },
  {
    name: "Sandhya Aggarwal",
    role: "Co-founder & Head of Student Services",
    photo: SANDHYA_PHOTO,
    credentials: ["Certified Education Counsellor", "British Council Certified Counsellor", "15+ Years at Mindways"],
    bio: "Sandhya leads operations, documentation, and process excellence across admissions and visa workflows. Families value her for precision, responsiveness, and calm control of complex timelines.",
  },
];

const Proof = () => {
  return (
    <section id="proof" className="py-20 md:py-28 bg-[#FDFCF8]">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#C9A227] mb-4">
            Why Families Trust Us
          </p>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-medium text-[#1A202C] tracking-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Built on <span className="text-[#2B4C7E]">Trust & Results</span>
          </h2>
        </motion.div>

        {/* Founder Profiles */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="text-center mb-10">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#C9A227] mb-3">
              Your Advisors
            </p>
            <h3
              className="text-2xl md:text-3xl font-medium text-[#1A202C]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Meet the Team Behind ESA
            </h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {founders.map((founder, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="bg-white p-8 rounded-[4px] border border-[#E2E8F0] hover:border-[#C9A227] hover:shadow-xl transition-all duration-300"
                data-testid={`founder-card-${index}`}
              >
                <div className="flex flex-col sm:flex-row gap-6">
                  <div className="flex-shrink-0">
                    <img
                      src={founder.photo}
                      alt={founder.name}
                      className="w-32 h-32 rounded-full object-cover border-4 border-[#F5F5F0] shadow-lg mx-auto sm:mx-0"
                    />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <h4
                      className="text-xl font-medium text-[#1A202C] mb-1"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {founder.name}
                    </h4>
                    <p className="text-[#C9A227] font-medium text-sm mb-3">
                      {founder.role}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4 justify-center sm:justify-start">
                      {founder.credentials.map((cred, i) => (
                        <span
                          key={i}
                          className="text-xs bg-[#2B4C7E]/10 text-[#2B4C7E] px-3 py-1 rounded-full"
                        >
                          {cred}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-[#4A5568] leading-relaxed">
                      {founder.bio}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mindways Legacy Note */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 text-center"
          >
            <div className="inline-flex items-center gap-2 bg-[#F5F5F0] px-6 py-3 rounded-full">
              <Award className="w-5 h-5 text-[#C9A227]" />
              <span className="text-sm text-[#4A5568]">
                Elite School Advisory is the premium K-12 admissions vertical of{" "}
                <strong className="text-[#1A202C]">Mindways</strong> (est. 2005)
              </span>
            </div>
          </motion.div>
        </motion.div>

        {/* Process Credibility */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {processPoints.map((point, index) => {
            const Icon = point.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="bg-white p-6 rounded-[4px] border border-[#E2E8F0] text-center hover:border-[#C9A227] transition-colors duration-300"
                data-testid={`process-point-${index}`}
              >
                <div className="w-12 h-12 rounded-full bg-[#2B4C7E]/10 flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-6 h-6 text-[#2B4C7E]" />
                </div>
                <h3
                  className="text-lg font-medium text-[#1A202C] mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {point.title}
                </h3>
                <p className="text-sm text-[#4A5568]">{point.description}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Testimonials */}
        <div className="mb-12">
          <div className="text-center mb-10">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#C9A227] mb-3">
              From Our Families
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="testimonial-card bg-white p-8 rounded-[4px] border border-[#E2E8F0] hover:shadow-lg transition-shadow duration-300"
                data-testid={`testimonial-${index}`}
              >
                <p className="text-[#4A5568] leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="text-sm font-medium text-[#1A202C]">
                    {testimonial.author}
                  </p>
                  <p className="text-xs text-[#4A5568]">{testimonial.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* What We Will Tell You */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-[#F5F5F0] p-8 md:p-10 rounded-[4px] border border-[#E2E8F0] max-w-3xl mx-auto"
        >
          <div className="text-center">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#C9A227] mb-4">
              Our Promise
            </p>
            <div className="border-l-4 border-[#C9A227] pl-6 text-left">
              <p
                className="text-lg md:text-xl text-[#2B4C7E] italic mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                "We will tell you the truth — even when it's not what you want to
                hear. We will recommend not applying where fit is wrong. That's how
                trust is built, and that's how families make decisions they don't
                regret."
              </p>
              <p className="text-sm text-[#4A5568]">— ESA Founding Principle</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Proof;
